// FinanceSection — Onglet Finance TEF : 2 flux (mockup-finance.html)
//  ENTRANT (client→TEF) = Facture AMO Pennylane + Règlements AMO
//  SORTANT (TEF→visiteur) = Visite payée au visiteur + date paiement + suivi €
//
// CC-DEV1-15 — migration <BlockCard> (cohérence des 4 onglets) :
//  - Sections Règlements AMO + Visite payée portées sur la primitive partagée.
//  - DOUBLON retiré : la grille 4 champs (Statut · Réf · Montant TTC · Date
//    règlement) n'existe plus QUE dans FactureAMOSection (ENTRANT). Le bloc
//    Règlements AMO ne garde que la liste multi-lignes (PaiementsMultiLignes),
//    sa vraie raison d'être. Plus de copie statut/réf/montant/date ici.
//  - Nouveau champ : date de paiement AU visiteur (SORTANT).
import { useState, useEffect } from "react";
import { CreditCard, Users, CalendarDays } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { BlockCard } from "../../../components/BlockCard";
import { API, getAuthHeaders, formatDate } from "./helpers";
import { toast } from "sonner";
import { FactureAMOSection } from "./FactureAMOSection";
import { hasIntegrationPennylane } from "../../../utils/tenantProfile";
import { PaiementsMultiLignes } from "./PaiementsMultiLignes";
import ChampDate from "../../../components/ui/champ-date";

export function FinanceSection({ dossier, dossierId, tenantId, isTEF, abandoned, onUpdate }) {
  const verrouille = dossier.verrouille || abandoned;
  const headers = getAuthHeaders(tenantId, "application/json");

  // SORTANT ouvert par défaut mais repliable (ex-<details open>). État local pour
  // garder le collapsible fonctionnel via la primitive BlockCard (open contrôlé).
  const [sortantOpen, setSortantOpen] = useState(true);

  // SORTANT — suivi € du visiteur de CE dossier (depuis visiteurs-stats, qui lit
  // tarif_visite de la fiche op_visiteurs). dû/payé/reste calculés côté backend.
  const [visiteurEuros, setVisiteurEuros] = useState(null);
  useEffect(() => {
    if (!isTEF || !dossier.visiteur_amo) { setVisiteurEuros(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/metier/hub-grids/visiteurs-stats`, {
          headers: getAuthHeaders(tenantId), credentials: "include",
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const target = (dossier.visiteur_amo || "").trim().toLowerCase();
        const match = (data.visiteurs || []).find(v =>
          (v.visiteur || "").trim().toLowerCase() === target
          || (v.visiteur || "").toLowerCase().includes(target)
          || target.includes((v.visiteur || "").toLowerCase())
        );
        if (!cancelled) setVisiteurEuros(match || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [isTEF, dossier.visiteur_amo, tenantId]);
  const fmtEur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

  // PATCH générique d'un champ du dossier (toggle visite payée, date paiement…).
  const patchDossier = async (body, okMsg) => {
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        method: "PATCH", headers, credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.ok) { if (okMsg) toast.success(okMsg); onUpdate?.(); }
      else toast.error("Erreur mise à jour");
    } catch { toast.error("Erreur réseau"); }
  };

  return (
    <>
      {/* ─── 1. FACTURE AMO (ENTRANT) — porte la grille 4 champs (source unique) ─── */}
      <div data-subnav-id="facture-amo">
        {isTEF && hasIntegrationPennylane() && <FactureAMOSection dossierId={dossierId} dossier={dossier} onUpdate={onUpdate} />}
      </div>

      {/* ─── 2. RÈGLEMENTS AMO (ENTRANT) — liste multi-lignes uniquement ─── */}
      {isTEF && <div data-subnav-id="paiement">
        <BlockCard
          collapsible
          icon={CreditCard}
          iconColor="var(--oh-hub-finance)"
          title="Règlements AMO (ENTRANT)"
          testId="bloc-paiement"
        >
          <PaiementsMultiLignes
            dossierId={dossierId}
            tenantId={tenantId}
            paiements={dossier?.paiements || []}
            montantAmo={dossier?.montant_amo}
            statutPaiement={dossier?.statut_paiement}
            statutFacturation={dossier?.statut_facturation}
            pennylaneStatus={dossier?.pennylane_invoice?.payment_status}
          />
        </BlockCard>
      </div>}

      {/* ─── 3. SORTANT : Visite payée AU visiteur + date paiement + suivi € ─── */}
      <div data-subnav-id="paiement-visiteur">
        <BlockCard
          collapsible
          open={sortantOpen}
          onToggle={(e) => setSortantOpen(e.currentTarget.open)}
          icon={Users}
          iconColor="var(--oh-stat-amber-text)"
          title="Visite payée (au visiteur) — SORTANT"
          testId="bloc-paiement-visiteur"
        >
          <div className="space-y-3">
            {/* Toggle visite_payee (TEF a versé au visiteur) */}
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: dossier.visite_payee ? "rgba(16,185,129,0.06)" : "var(--oh-bg-surface)", border: `1px solid ${dossier.visite_payee ? "rgba(16,185,129,0.2)" : "var(--oh-border)"}` }} data-testid="finance-visite-payee">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" style={{ color: dossier.visite_payee ? "#10b981" : "var(--oh-text-muted)" }} />
                <span className="text-sm" style={{ color: "var(--oh-text)" }}>Payée au visiteur{dossier.visiteur_amo ? ` — ${dossier.visiteur_amo}` : ""}</span>
              </div>
              {!verrouille ? (
                <button
                  onClick={() => patchDossier(
                    { visite_payee: !dossier.visite_payee },
                    dossier.visite_payee ? "Visite marquée non payée" : "Visite marquée payée",
                  )}
                  data-testid="toggle-visite-payee"
                  aria-label={dossier.visite_payee ? "Marquer la visite non payée au visiteur" : "Marquer la visite payée au visiteur"}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${dossier.visite_payee ? "bg-emerald-500" : "bg-zinc-600"}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${dossier.visite_payee ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              ) : (
                <Badge className="text-xs border-0" style={{ background: dossier.visite_payee ? "var(--oh-stat-green-bg)" : "var(--oh-stat-slate-bg)", color: dossier.visite_payee ? "var(--oh-stat-green-text)" : "var(--oh-stat-slate-text)" }}>
                  {dossier.visite_payee ? "Oui" : "Non"}
                </Badge>
              )}
            </div>

            {/* Dû au visiteur pour CE dossier (détail) : surcharge dossier sinon
                tarif de base de la fiche (défaut 120). Le CUMUL du visiteur est
                dans sa fiche (Opération › Visiteurs), pas ici. */}
            <div className="rounded-lg p-3 space-y-2" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }} data-testid="suivi-euros-visiteur">
              <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>Dû au visiteur — cette visite</div>
              {(() => {
                const base = visiteurEuros?.tarif_visite ?? 120;
                const override = dossier.tarif_visite_override;
                const effective = override != null ? override : base;
                return (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span style={{ color: "var(--oh-text-muted)" }}>Tarif appliqué : <b style={{ color: "var(--oh-text)" }}>{fmtEur(effective)}</b>
                      {override != null
                        ? <span className="text-[10px] ml-1" style={{ color: "var(--oh-stat-amber-text)" }}>(surcharge dossier)</span>
                        : <span className="text-[10px] ml-1" style={{ color: "var(--oh-text-muted)" }}>(base visiteur {fmtEur(base)})</span>}
                    </span>
                    <span style={{ color: "var(--oh-text-muted)" }}>Payé au visiteur : <b style={{ color: dossier.visite_payee ? "#10b981" : "var(--oh-stat-amber-text)" }}>{dossier.visite_payee ? "Oui" : "Non"}</b></span>
                  </div>
                );
              })()}

              {/* CC-DEV1-15 — Date de paiement AU visiteur (nouveau champ SORTANT). */}
              <div className="flex items-center gap-2 text-sm pt-1">
                <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--oh-text-muted)" }} />
                <span style={{ color: "var(--oh-text-muted)" }}>Date de paiement :</span>
                {!verrouille ? (
                  <ChampDate
                    
                    defaultValue={dossier.date_paiement_visiteur ?? ""}
                    onBlur={(e) => {
                      const v = e.target.value || null;
                      if ((dossier.date_paiement_visiteur ?? null) === (v ?? null)) return;
                      patchDossier({ date_paiement_visiteur: v }, "Date de paiement visiteur enregistrée");
                    }}
                    className="rounded-md px-2 py-1 text-sm"
                    style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                    data-testid="input-date-paiement-visiteur"
                    aria-label="Date de paiement au visiteur"
                  />
                ) : (
                  <b style={{ color: "var(--oh-text)" }} data-testid="date-paiement-visiteur-ro">
                    {dossier.date_paiement_visiteur ? formatDate(dossier.date_paiement_visiteur) : "—"}
                  </b>
                )}
              </div>

              {!verrouille && (
                <div className="flex items-center gap-2 text-sm">
                  <span style={{ color: "var(--oh-text-muted)" }}>Surcharge (€, ce dossier) :</span>
                  <input
                    type="number" defaultValue={dossier.tarif_visite_override ?? ""}
                    placeholder={`base ${visiteurEuros?.tarif_visite ?? 120}`}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      const v = raw === "" ? null : parseFloat(raw);
                      if ((dossier.tarif_visite_override ?? null) === (v ?? null)) return;
                      patchDossier({ tarif_visite_override: v }, "Surcharge tarif visiteur enregistrée");
                    }}
                    className="w-28 rounded-md px-2 py-1 text-sm"
                    style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                    data-testid="input-tarif-override"
                    aria-label="Surcharge du tarif visiteur pour ce dossier"
                  />
                  <span className="text-[10px]" style={{ color: "var(--oh-text-muted)" }}>vide = tarif de base</span>
                </div>
              )}
              <p className="text-[10px]" style={{ color: "var(--oh-text-muted)" }}>Cumul dû/payé/reste du visiteur → sa fiche (Opération › Visiteurs).</p>
            </div>
          </div>
        </BlockCard>
      </div>
    </>
  );
}
